// mailUtils.js
// Microsoft Graph/mail helpers

import axios from "axios";
import { Client } from "@microsoft/microsoft-graph-client";

import { debugLog, errorLog } from "./debugUtils";
import { getAccessToken } from "./sessionUtils";

/**
 * Sends an email via Microsoft Graph API.
 * @async
 * @param {Object} data - Email data.
 * @param {string} data.subject - Email subject.
 * @param {string} data.message - HTML content for the message.
 * @param {string} data.to - Comma-separated recipient addresses.
 * @param {string|null} [data.cc] - Comma-separated CC addresses.
 * @param {string|null} [data.bcc] - Comma-separated BCC addresses.
 * @returns {Promise<Object>} Result object with success and message.
 *
 * @example
 * await sendMail_MS_Graph({
 *   subject: "Test Subject",
 *   message: "<b>Hello</b>",
 *   to: "a@b.com,c@d.com",
 *   cc: "",
 *   bcc: ""
 * });
 */
export const sendMail_MS_Graph = async (data = {}) => {
	const {
		subject = "Send mail via API (default subject)",
		message = "<h2>Send mail via API (default content)</h2>",
		to = "yle@msim.metrea.aero",
		cc = "",
		bcc = "",
	} = data;

	const request = { scopes: ["Mail.Send"] };
	const accessToken = await getAccessToken(request);

	const client = Client.init({
		authProvider: (done) => done(null, accessToken),
	});

	// Helper to split and clean comma-separated emails
	const parseEmails = (emails) =>
		(emails || "")
			.replace(/\s/g, "")
			.split(",")
			.filter(Boolean)
			.map((address) => ({ emailAddress: { address } }));

	const toRecipients = parseEmails(to);
	const ccRecipients = parseEmails(cc);
	const bccRecipients = parseEmails(bcc);

	debugLog(2, "[mailUtils][FLOW] sendMail_MS_Graph started", {
		data: data,
		toRecipients: toRecipients,
		ccRecipients: ccRecipients,
		bccRecipients: bccRecipients,
	});

	const mailMessage = {
		message: {
			subject:
				subject +
				(process.env.REACT_APP_ENV !== "prod_server"
					? ` (${process.env.REACT_APP_ENV} - MS Graph)`
					: ""),
			body: {
				contentType: "Html",
				content: message + emailFooter(),
			},
			toRecipients,
			ccRecipients,
			bccRecipients,
		},
	};

	try {
		await client.api("/me/sendMail").post(mailMessage);
		debugLog(3, "[mailUtils][STATE] sendMail_MS_Graph succeeded", { to: data.to });
		return { success: true, message: "Email sent successfully", error: null };
	} catch (error) {
		errorLog(2, "[mailUtils][FLOW] sendMail_MS_Graph failed", { error: error });
		return { success: false, message: "Error sending email", error: error };
	}
};

/**
 * Sends an email using the backend API endpoint.
 * @async
 * @param {Object} data - Email data, same format as sendMail_MS_Graph.
 * @returns {Promise<void>}
 *
 * @example
 * await sendMail_API({
 *   subject: "Subject",
 *   message: "Content",
 *   to: "a@b.com",
 *   cc: "",
 *   bcc: ""
 * });
 */
export const sendMail_API = async (
	data = {
		subject: "Send mail via API (default subject)",
		message: "<h2>Send mail via API (default content)</h2>",
		to: "yle@msim.metrea.aero",
		cc: null,
		bcc: null,
	},
) => {
	try {
		debugLog(2, "[mailUtils][FLOW] sendMail_API started", { data: data });

		// Await make wait until that
		// promise settles and return its result
		await axios
			.post(`${process.env.REACT_APP_SERVER_URI}/api/sendmail`, data, {
				headers: {
					"Content-type": "application/json",
				},
				withCredentials: true,
			})
			.then(() => {
				debugLog(3, "[mailUtils][STATE] sendMail_API succeeded", {});
			})
			.catch((error) => {
				errorLog(2, "[mailUtils][FLOW] sendMail_API request failed", { error: error });
			});
	} catch (error) {
		errorLog(2, "[mailUtils][FLOW] sendMail_API failed", { error: error });
	}
};

/**
 * Generates an email footer based on the current environment.
 * @returns {string} HTML footer string.
 */
export const emailFooter = () => {
	let strServer = "";

	switch (process.env?.REACT_APP_ENV) {
		case "dev_server":
			strServer = "Raven Development Server";
			break;

		case "stage_server":
			strServer = "Raven Staging Server";
			break;

		case "prod_server":
			strServer = "Raven Production Server";
			break;

		default:
			strServer = "Raven Application Server";
			break;
	}

	let strOutput = `
<br /><br /><br />
<div style="font-weight: 600; margin-top: 6em;">
Email sent from <a href="${process.env.REACT_APP_URI}">${strServer}</a>
</div>
`;

	return strOutput;
};
